/**
 * Draws 往期列表每一行的取值。
 *
 * <p>这一页的立身之本是「屏幕上每个数你都能自己核」。所以取值全部集中在这里：
 * **列表行显示的序号和验算页算出的序号必须是同一次计算的结果**。原型阶段这两处
 * 各存一份，结果同一期在列表上是 #112、点进去是 #058 —— 一个自相矛盾的页面比
 * 没有这一页更糟。
 *
 * <p><b>`drawnAt` 这个字段名不能改。</b> `shared/milhar/historyDays.ts` 的
 * `groupByLocalDay<T extends { drawnAt: number }>` 按这个名字取时间戳，
 * 换个名字就复用不了它，于是「按天分隔」这件事会被重写第二遍。
 */

import { settledPoolOf } from '../game/rafflePrize.ts';
import { drawnSerialFrom } from '../lib/raffleDrawnSerial.ts';
import type { RaffleRecentEvent } from '../../features/wallet/api.ts';

export type DrawHistoryRow = {
  eventId: string;
  /** 开奖时刻，秒。字段名由 `groupByLocalDay` 的约束决定 */
  drawnAt: number;
  pool: number;
  /** `null` 表示算不出来 —— 调用方应当**整段不渲染** */
  drawnSerial: number | null;
  winnerCount: number;
  totalEntries: number;
  serialBase: number;
  voided: boolean;
  voidedReason: string;
  anchorRound: number;
  dDigits: string;
};

function num(value: unknown): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/**
 * 一行要显示的全部取值。
 *
 * <p><b>没有期号。</b> 2026-09-18 起抽奖列表不显示日内期号：它和票号都是
 * 「#三位数」，同屏出现玩家分不清哪个是期、哪个是中奖的票；而它借的是房间
 * 五分钟一局的编号（`raffleAnchorRound`），每小时一期就跳 12，一天只出现
 * 012/024/…/288 这 24 个值，本身也读不出顺序。一期由「这一天 + 这个时刻」
 * 认（每个国家一个节目、每小时一期），要一个可引用的技术号时验算页有
 * global 号。
 */
export function historyRow(event: RaffleRecentEvent): DrawHistoryRow {
  const voided = String(event.status || '') === 'voided';
  const winners = event.winners || [];
  const totalEntries = num(event.total_entries);
  const serialBase = event.serial_base === 1 ? 1 : 0;
  const dDigits = String(event.d_digits || '').replace(/\D/g, '');
  return {
    eventId: String(event.event_id || event.id || ''),
    drawnAt: num(event.scheduled_at),
    // 流拍的期没有奖池可言 —— 票退回去了。显示计划中的池额是假话。
    //
    // 已结算的期，池额 = winners[].amount 之和 —— 那是真正付出去的钱,与下面
    // 渲染的中奖名单是同一份数据,对得上账。不能用 poolOf(prize_brackets)：
    // 那个函数只读 brackets[0],而程序按 total_entries 落在三档里的一档结算
    // （V138__raffle_program_configuration.sql），2026-09-13 复核 CRITICAL——
    // 30 个参与者结算按第 2 档付了 R$300、7 个赢家,这一行却印着第 0 档的
    // R$100。没有中奖记录时（理论上不该发生在已结算的期上,但防御性地）才退
    // 回按 total_entries 选档算出的池额。
    pool: voided ? 0 : settledPoolOf(winners, event.prize_brackets, totalEntries),
    drawnSerial: voided ? null : drawnSerialFrom(winners[0]?.serial_no, dDigits, totalEntries, serialBase),
    winnerCount: winners.length,
    totalEntries,
    serialBase,
    voided,
    voidedReason: String(event.voided_reason || ''),
    anchorRound: num(event.anchor_round_number),
    dDigits,
  };
}

/** 这一期有没有我，中没中。 */
export function mineMark(event: RaffleRecentEvent): 'won' | 'played' | 'none' {
  const entries = event.my_entries || [];
  if (entries.length === 0) return 'none';
  // 这道 null/undefined 判空是防御性的、和下面的 Number.isFinite(prize) && prize > 0
  // 是冗余的 —— Number(null) 是 0、Number(undefined) 是 NaN，两条路本来就都落不到
  // 'won'。留着它只是为了让「没有奖金」这个分支读起来是显式的空值判断，而不必让
  // 下一个读者自己去心算一遍 Number() 对 null/undefined 的强制转换规则。
  const prize = event.my_prize === null || event.my_prize === undefined
    ? 0 : Number(event.my_prize);
  return Number.isFinite(prize) && prize > 0 ? 'won' : 'played';
}

/**
 * 这一列竖线该不该渲染。
 *
 * <p>**整列没有参与数据时必须整列收起，而不是全渲染成灰色。** 2026-09-11 Milhar
 * 那轮终审的 CRITICAL 就是这一条：所有登录玩家都看到一整列灰条，等于逐行告诉
 * 每个人「你一期都没玩过」。
 *
 * <p><b>判断条件不能是 `my_entries !== undefined`。</b> `toRecentJson`（连带
 * History 专用的 `toHistoryJson`）**总是**写这个字段——没参与就是空数组，
 * 不是缺席——所以那道判断永远为真，这道防线从来没生效过：一个刚注册、一期
 * 都没玩过的新玩家打开 History，看到的就是上面那整列灰条（2026-09-13 复核
 * Minor）。必须看**有没有哪一期的 `my_entries` 非空**，不是这个字段存不存在。
 */
export function hasMineData(events: readonly RaffleRecentEvent[]): boolean {
  return events.some((event) => (event.my_entries || []).length > 0);
}

/**
 * 名次的序号：第一名之后连号顺延，过最后一号（`totalEntries - 1`）回到 0。
 *
 * <p>规则出自后端 `JdbcRaffleRepository.winnerSerials`，与验算页第 4 步、
 * 参与名单的中奖标记共用这一份 —— 三处各写一遍就是三份可能各自漂移的算法。
 */
export function serialWrap(first: number, totalEntries: number, count: number, serialBase = 0): number[] {
  // totalEntries 为 0 时不存在「第几号」；取模会抛或得 NaN，两条路都会变成假号码。
  if (!Number.isFinite(totalEntries) || totalEntries <= 0) return [];
  // 票号 0 起，所以 0 是合法的第一张票；负数才是「没有」。
  const base = serialBase === 1 ? 1 : 0;
  if (!Number.isFinite(first) || first < base) return [];
  if (!Number.isFinite(count) || count <= 0) return [];
  return Array.from({ length: Math.trunc(count) },
    (_, k) => base + ((Math.trunc(first) - base + k) % Math.trunc(totalEntries)));
}

/**
 * 「我的」页签里，我在这一期的结果。
 *
 * <p>`pending` 是「还没开」—— 我报名了、这一期还没跑。它和 `lost` 必须分开：
 * 把一期还没开的期写成「未中」，是在替一件没发生的事下结论（2026-09-17 PM
 * 明确要求状态自成一列，并且未开奖的期盾牌置灰）。
 */
export type MyDrawOutcome = 'won' | 'lost' | 'refunded' | 'pending';

export type MyDrawRow = DrawHistoryRow & {
  /** 我在这一期一共几张票 */
  ticketCount: number;
  /**
   * 我在这一期的全部票号（1 起，已换算），**不截断**。
   *
   * <p>2026-09-17 PM 明确要求不精简：玩家拿这些号去对中奖名单，折掉任何一个
   * 都可能正是他要找的那个。渲染时换行铺开，不是省略。
   */
  tickets: { serial: number; won: boolean }[];
  prize: number;
  outcome: MyDrawOutcome;
};

/**
 * 「我的」页签一行的取值。
 *
 * <p><b>票号不做换算。</b> 和 `drawnSerialFrom` 同一个理由：票号 0 起，线上与
 * 展示同一个值。这里是玩家自己的票号，印错一位比印错开奖号更糟 —— 他会拿着
 * 这个号去对中奖名单。
 *
 * <p><b>中奖的票排在前面。</b> 持 10 张票时那一串号要换好几行，把中的那张排在
 * 第一个，玩家一眼就知道是哪张，不必自己在十个号里找那个金色的。同类再按票号
 * 升序。
 *
 * <p><b>哪张票中了看 `entry_id`，不看票号。</b> 主键能精确对上，票号是展示值；
 * 即便现在两边不再有换算，拿展示值做匹配也是把可靠的换成可能漂移的。
 */
export function mineRow(event: RaffleRecentEvent): MyDrawRow {
  const base = historyRow(event);
  const entries = event.my_entries || [];
  const winningEntryIds = new Set(
    (event.winners || [])
      .map((winner) => String(winner.entry_id ?? ''))
      .filter((id) => id !== ''),
  );
  const tickets = entries.map((entry) => ({
    serial: Math.trunc(num(entry.serial_no)),
    won: winningEntryIds.has(String(entry.entry_id ?? '')),
  }));
  tickets.sort((a, b) => (a.won === b.won ? a.serial - b.serial : (a.won ? -1 : 1)));

  const prize = num(event.my_prize);
  // 判定顺序是有讲究的：流拍和未开奖都优先于「没中」—— 票退了、或者根本还
  // 没开，这两种情况说「未中」都是在替一件没发生过的事下结论。
  const settled = String(event.status || '').toLowerCase() === 'settled';
  const outcome: MyDrawOutcome = base.voided
    ? 'refunded'
    : !settled ? 'pending' : (prize > 0 ? 'won' : 'lost');

  return {
    ...base,
    ticketCount: tickets.length,
    tickets,
    prize,
    outcome,
  };
}
